import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import classnames from 'classnames';
import { withStyles, Typography } from '@material-ui/core';
import { Droppable, Draggable } from 'react-beautiful-dnd';

import {
    datestamp, msInOneDay,
} from '../../../youwell-common/dateUtils';

import TaskAvatar from './TaskAvatar';
import { getLocalDateOnlyString, skillStartHour } from './utils';

const itemHeight = 50;

const styles = theme => ({
    root: {
        display: 'flex',
    },
    day: {
        flex: '1 1 20%',
        display: 'flex',
        flexDirection: 'column',
    },
    dayHeader: {
        height: itemHeight,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.common.white,
        background: theme.colors.blue4,
        borderRight: theme.borders.normal,
    },
    firstDayHeader: {
        borderLeft: theme.borders.normal,
    },
    dayTasks: {
        flex: '1 1 auto',
        borderRight: theme.borders.normal,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        minHeight: 120,
        paddingBottom: theme.spacing(1),
    },
    firstDay: {
        borderLeft: theme.borders.normal,
    },
    availableDay: {
        background: theme.colors.grey2,
    },
    dayLetter: {
        textTransform: 'uppercase',
    },
    task: {
        marginTop: theme.spacing(1),
    },
    draggingOver: {
        border: theme.borders.dashed,
    },
});

class WeekPlan extends React.Component {
    render() {
        const {
            classes, translate, weekStart, tasks, taskNames, availableDays, disabled,
        } = this.props;

        const weekArray = Array.from(Array(5).keys(), (n) => {
            const d = new Date(weekStart.getTime() + n * msInOneDay);
            d.setHours(skillStartHour, 0, 0, 0);
            const dateString = getLocalDateOnlyString(d);
            return {
                date: d,
                datestamp: dateString,
                tasks: tasks[dateString],
            };
        });

        return (
            <div className={classes.root}>
                {weekArray.map((day, i) => (
                    <div key={day.date} className={classes.day}>
                        <div className={classnames(classes.dayHeader, { [classes.firstDayHeader]: i === 0 })}>
                            <Typography variant="caption" color="inherit">
                                {datestamp(day.date).slice(0, 5)}
                            </Typography>
                            <Typography variant="subtitle1" color="inherit" className={classes.dayLetter}>
                                {translate(`time.weekdays.${day.date.getDay()}`).slice(0, 1)}
                            </Typography>
                        </div>
                        <Droppable droppableId={day.datestamp}>
                            {(provided, snapshot) => (
                                <div
                                    className={classnames(classes.dayTasks, {
                                        [classes.firstDay]: i === 0,
                                        [classes.availableDay]: availableDays[(i + 1).toString()],
                                        [classes.draggingOver]: snapshot.isDraggingOver,
                                    })}
                                    ref={provided.innerRef}
                                    {...provided.droppableProps}
                                >
                                    {day?.tasks?.map((task, taskIndex) => (
                                        <Draggable key={task.taskId} draggableId={`${day.datestamp}-${task.taskId}`} index={taskIndex} isDragDisabled={disabled}>
                                            {dragProvided => (
                                                <div
                                                    ref={dragProvided.innerRef}
                                                    style={dragProvided.draggableProps.style}
                                                    {...dragProvided.draggableProps}
                                                    {...dragProvided.dragHandleProps}
                                                >
                                                    <TaskAvatar task={taskNames[task.taskId]} className={classes.task} />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </div>
                ))}
            </div>
        );
    }
}

WeekPlan.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    weekStart: PropTypes.object.isRequired,
    tasks: PropTypes.object.isRequired,
    taskNames: PropTypes.object.isRequired,
    availableDays: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
};

WeekPlan.defaultProps = {
    disabled: false,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
});

export default connect(mapStateToProps)(withStyles(styles)(WeekPlan));
